import React, { useEffect, useRef, useState } from 'react';

import {
  platformAuthenticatorIsAvailable,
  startAuthentication,
} from '@simplewebauthn/browser';
import cn from 'classnames';
import { useForm } from 'react-hook-form';
import { Link, useLocation } from 'react-router-dom';

import { ALERT_ERROR } from 'components/AlertNotifications/AlertNotificationProvider';
import useAlertNotification from 'components/AlertNotifications/useAlertNotification';
import Spinner from 'components/Spinner';
import { SESSION_STORAGE_OAUTH_ACTION } from 'config/constants';
import SpinnerIcon from 'design/Icons/SpinnerIcon';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { userShouldConfigTwoFa } from 'lib/twofa';
import { AuthError } from 'models/Errors';
import { User } from 'models/User';
import { LazyPreload } from 'routes/helpers';
import { userSignInSuccess } from 'store/modules/auth';
import {
  selectOauthAction,
  selectOauthMessage,
  selectOauthProvider,
} from 'store/modules/oauth';
import { capitalizeFirstLetter } from 'utils/helpers';
import { apiGet, apiPost } from 'utils/request';
import { isCacheFly, PORTAL_LINK } from 'utils/whiteLabeling';

import ChooseLoginMethod from './ChooseLoginMethod';
import { TwoFaType } from './helpers';
import OauthButton from './OauthButton';
import SSOLogin from './SSOLogin';

import styles from './SignIn.module.scss';

const ForgotPassPage = LazyPreload(() => import('containers/Pages/ForgotPassPage'));

interface FormData {
  username: string;
  password: string;
}

interface SignInProps {
  setShowChangePassword: (value: boolean) => void;
  setShowTwoFaRequired: (value: boolean) => void;
  setShowTwoFa: (value: TwoFaType | null) => void;
  setShowEnableBiometrics: (value: boolean) => void;
  setUser: (value: User) => void;
}

const SignIn = ({
  setShowChangePassword,
  setShowTwoFaRequired,
  setUser,
  setShowTwoFa,
  setShowEnableBiometrics,
}: SignInProps) => {
  const { register, handleSubmit, reset } = useForm<FormData>();
  const [pending, setPending] = useState(false);
  const [oauthPending, setOauthPending] = useState('');
  const [error, setError] = useState('');
  const dispatch = useAppDispatch();
  const credentialID = localStorage.getItem('storedCredentialId');
  const hasCredentials = !!credentialID;
  const [showMethodChooser, setShowMethodChooser] = useState(hasCredentials);
  const [checkedDeviceLogin, setCheckedDeviceLogin] = useState(!hasCredentials);
  const v1LoginForm = useRef<HTMLFormElement>(null);
  const v1LoginFormUsername = useRef<HTMLInputElement>(null);
  const v1LoginFormPassword = useRef<HTMLInputElement>(null);
  const [showSSOLogin, setShowSSOLogin] = useState(false);

  const location = useLocation<{ username?: string; password?: string }>();
  const notify = useAlertNotification();

  const clearHistory = () => {
    window.history.replaceState({}, document.title);
  };

  const oauthAction = useAppSelector(selectOauthAction);
  const oauthMessage = useAppSelector(selectOauthMessage);
  const oauthProvider = useAppSelector(selectOauthProvider);

  const oauthManualLoginRequired = oauthAction === 'manualLoginRequired';

  useEffect(() => {
    const username = location.state?.username;
    const password = location.state?.password;
    if (username && password) {
      reset({
        username,
        password,
      });
      onSubmit({ username, password });
    }

    window.addEventListener('beforeunload', clearHistory);
    return () => {
      clearHistory();
      window.removeEventListener('beforeunload', clearHistory);
    };
  }, []);

  const oauthManualLogin = async () => {
    const res = await apiGet(`/users/me/${oauthProvider}/link`);
    sessionStorage.setItem(SESSION_STORAGE_OAUTH_ACTION, 'linking');
    window.open(res.authUrl, '_self');
  };

  const onSubmit = async ({ username, password }: FormData) => {
    if (pending) return;
    setPending(true);
    try {
      const authRes = await apiPost(
        '/auth/login',
        {
          username: username.toLowerCase(),
          password,
        },
        { action: 'login', withCaptcha: true },
      );

      console.log('Login outcome is ', authRes);
      sessionStorage.setItem('auth', JSON.stringify({ token: authRes.token, user: {} }));

      const userData = await apiGet('/users/me', { action: 'getUser' });

      setUser(userData);

      sessionStorage.setItem(
        'auth',
        JSON.stringify({ token: authRes.token, user: userData }),
      );

      if (userData.passwordChangeRequired && !oauthManualLoginRequired) {
        const hasPermission = userData.permissions.find(
          (p: string) => p === 'P_USER_PROFILE_MANAGE',
        );

        if (hasPermission) {
          setShowChangePassword(true);
          return;
        }
      }

      if (userShouldConfigTwoFa(userData) && !oauthManualLoginRequired) {
        setShowTwoFaRequired(true);
        return;
      }

      const credentialID = localStorage.getItem('storedCredentialId');
      const rememberDevicePrompt = localStorage.getItem('rememberDevicePrompt');
      const deviceLoginSupported = await platformAuthenticatorIsAvailable();
      const askEnableBiometrics =
        !rememberDevicePrompt && !credentialID && deviceLoginSupported;

      if (askEnableBiometrics && !oauthManualLoginRequired) {
        setShowEnableBiometrics(true);
      } else {
        if (oauthManualLoginRequired) {
          oauthManualLogin();
        } else {
          dispatch(userSignInSuccess(userData));
        }
      }
    } catch (e) {
      console.log(e);
      const error = e as AuthError;
      if (error.url) {
        if (error.method === 'POST') {
          v1LoginForm.current!.action = error.url;
          v1LoginFormUsername.current!.value = username.toLowerCase();
          v1LoginFormPassword.current!.value = password;
          v1LoginForm.current!.submit();
          return;
        } else {
          window.location.href = error.url;
        }
      }
      if (error.twoFactorAuthenticationRequired) {
        const { credentialOptions } = error;
        setShowTwoFa({ username: username.toLowerCase(), password, credentialOptions });
      } else {
        setError(error.message || 'Failed to log in');
      }
    } finally {
      setPending(false);
    }
  };

  const oauthLogin = async (provider: string) => {
    if (oauthPending) return;

    if (provider === 'SSO') {
      setShowSSOLogin(true);
      return;
    }

    try {
      setOauthPending(provider);
      const res = await apiGet(`/auth/${provider}`);
      window.open(res.authUrl, '_self');
    } catch (e) {
      console.log(e);
      notify(
        `${capitalizeFirstLetter(provider)} login error`,
        `Failed to login with your ${capitalizeFirstLetter(provider)} account`,
        ALERT_ERROR,
      );
    } finally {
      setOauthPending('');
    }
  };

  const handleBiometrics = async () => {
    setError('');
    const { credentialOptions } = await apiPost('/auth/deviceLogin', {
      credentialID,
    });

    try {
      const credential = await startAuthentication(credentialOptions);
      const authRes = await apiPost('/auth/deviceLogin', { credential });

      sessionStorage.setItem('auth', JSON.stringify({ token: authRes.token, user: {} }));
      console.log('Login outcome is ', authRes);
      console.log('account is ', authRes.account);

      const userData = await apiGet('/users/me', { action: 'getUser' });
      setUser(userData);

      sessionStorage.setItem(
        'auth',
        JSON.stringify({ token: authRes.token, user: userData }),
      );

      if (userShouldConfigTwoFa(userData) && !oauthManualLoginRequired) {
        setShowTwoFaRequired(true);
        return;
      }

      if (oauthManualLoginRequired) {
        oauthManualLogin();
      } else {
        dispatch(userSignInSuccess(userData));
      }

      setShowMethodChooser(false);
    } catch (e) {
      setError('Failed to authenticate via biometrics');
    }
  };

  const handlePassword = () => {
    setShowMethodChooser(false);
    setCheckedDeviceLogin(true);
  };

  if (showMethodChooser) {
    return (
      <ChooseLoginMethod
        error={error}
        onChooseBiometrics={handleBiometrics}
        onChoosePassword={handlePassword}
      />
    );
  }

  if (!checkedDeviceLogin) {
    return <Spinner />;
  }

  if (showSSOLogin) {
    return <SSOLogin />;
  }

  return (
    <div className="panel user-auth-card">
      <div className="panel-header">
        <h2>Log in to your account</h2>
        {error && <div className="alert alert-danger">{error}</div>}
      </div>
      {oauthManualLoginRequired && oauthMessage && (
        <div className="instructions mb-4">
          <p>{oauthMessage}</p>
        </div>
      )}
      <div className="panel-body">
        <form className={styles.form}>
          <div className="form-field form-field-lg">
            <div className="input">
              <label>Username:</label>
              <input
                type="text"
                autoFocus
                {...register('username', { required: true })}
                style={{ textTransform: 'lowercase' }}
                className={styles.input}
              />
            </div>
          </div>
          <div className="form-field form-field-lg mb-0">
            <div className="input">
              <label>Password:</label>
              <input
                type="password"
                {...register('password', { required: true })}
                className={styles.input}
              />
            </div>
          </div>
          <div
            className={styles.forgotPassLink}
            onMouseEnter={() => {
              ForgotPassPage.preload();
            }}
          >
            {isCacheFly && (
              <Link className="p-link-btn " to="/forgotpass">
                Forgot your password?
              </Link>
            )}
          </div>
          <div className="form-field mb-0">
            <button
              type="submit"
              className={cn('btn btn-primary btn-lg', styles.submitBtn)}
              onClick={handleSubmit(onSubmit)}
            >
              {pending && <SpinnerIcon className={styles.spinner} />}
              Log in
            </button>
          </div>
          {isCacheFly && !oauthManualLoginRequired && (
            <>
              <div className={styles.divider}>or</div>
              <div className={styles.oauthButtons}>
                <OauthButton
                  provider="google"
                  oauthPending={oauthPending}
                  oauthLogin={oauthLogin}
                />
                <OauthButton
                  provider="microsoft"
                  oauthPending={oauthPending}
                  oauthLogin={oauthLogin}
                />
                <OauthButton
                  provider="github"
                  oauthPending={oauthPending}
                  oauthLogin={oauthLogin}
                />
                <OauthButton
                  provider="SSO"
                  oauthPending={oauthPending}
                  oauthLogin={oauthLogin}
                />
              </div>
            </>
          )}
        </form>
      </div>

      <form ref={v1LoginForm} method="POST" action={`${PORTAL_LINK}/portal/login`}>
        <input
          type="hidden"
          ref={v1LoginFormUsername}
          name="login"
          style={{ textTransform: 'lowercase' }}
        />
        <input type="hidden" ref={v1LoginFormPassword} name="password" />
      </form>
    </div>
  );
};

export default SignIn;
